import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import moment, { Moment } from 'moment'
import { Subscription } from 'rxjs'
import { LabelToId } from 'src/app/common/input-utils'
import { Datum } from 'src/app/models/common/datum'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'

type PlanManagement = 'self' | 'ndiaManaged' | 'planManaged'
type PhiCountry = 'australian' | 'overseas'

// Step 2 — NDIS plan & duration (story e8-2). Renders one of two mutually-exclusive branches
// (has-NDIS fields vs applying/PHI fields) decided once in ngOnInit from the payload's
// ndis.hasNdis — the shell's *ngIf tears this component down whenever currentStepId changes, so a
// fresh read on init is always correct (PlanTypeStepComponent's own established pattern).
@Component({
  selector: 'app-service-plan-ndis-plan-step',
  templateUrl: './ndis-plan-step.component.html',
  styleUrls: ['./ndis-plan-step.component.scss'],
  standalone: false,
})
export class NdisPlanStepComponent
  implements OnInit, OnChanges, OnDestroy, ServicePlanStepComponent
{
  // Client-record prefill source for the NDIS number field — passed by the shell alongside
  // clientInformationAttributes (same underlying ClientDto.ndisNumber, two separate bindings).
  @Input() ndisNumber?: string | null

  hasNdis = false
  isNdisNumberPrefilled = false

  form!: FormGroup<{
    ndisNumber: FormControl<string | null>
    planManagement: FormControl<PlanManagement | null>
    planManagerName: FormControl<string | null>
    planManagerAddress: FormControl<string | null>
    planManagerContactName: FormControl<string | null>
    planManagerContactEmailPhone: FormControl<string | null>
    applyingForNdis: FormControl<boolean | null>
    hasPhi: FormControl<boolean | null>
    phiCountry: FormControl<PhiCountry | null>
    phiInsurerName: FormControl<string | null>
    phiMemberNo: FormControl<string | null>
    startDate: FormControl<Moment | null>
    endDate: FormControl<Moment | null>
  }>

  ndisNumberLabel = ''
  ndisNumberControlId = ''
  planManagementLabel = ''
  planManagementOptions: Datum[] = []
  phiCountryLabel = ''
  phiCountryOptions: Datum[] = []
  applyingForNdisLabel = ''
  hasPhiLabel = ''
  startDateLabel = ''
  endDateLabel = ''

  private subscription?: Subscription

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    const payload = this.state.getPayload().ndis
    this.hasNdis = payload.hasNdis === true

    this.ndisNumberLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_PLAN.NDIS_NUMBER_LABEL',
    )
    this.ndisNumberControlId = LabelToId(this.ndisNumberLabel)
    this.planManagementLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_PLAN.PLAN_MANAGEMENT_LABEL',
    )
    this.planManagementOptions = [
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_PLAN.PLAN_MANAGEMENT_SELF',
        ),
        value: 'self',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_PLAN.PLAN_MANAGEMENT_NDIA',
        ),
        value: 'ndiaManaged',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_PLAN.PLAN_MANAGEMENT_PLAN',
        ),
        value: 'planManaged',
      },
    ]
    this.applyingForNdisLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_PLAN.APPLYING_FOR_NDIS_LABEL',
    )
    this.hasPhiLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_PLAN.HAS_PHI_LABEL',
    )
    this.phiCountryLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_PLAN.PHI_COUNTRY_LABEL',
    )
    this.phiCountryOptions = [
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_PLAN.PHI_COUNTRY_AUSTRALIAN',
        ),
        value: 'australian',
      },
      {
        label: this.translate.instant(
          'SERVICE_PLAN.STEP.NDIS_PLAN.PHI_COUNTRY_OVERSEAS',
        ),
        value: 'overseas',
      },
    ]
    this.startDateLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_PLAN.START_DATE_LABEL',
    )
    this.endDateLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.NDIS_PLAN.END_DATE_LABEL',
    )

    // Both sides of this prefill decision must agree on what counts as "no value yet" — an empty
    // string from the payload is treated the same as null/undefined (AC10's "not prefilled, no
    // badge" case), not as a real answer that should block the shell's client-record value. The
    // client-record value (this.ndisNumber) only ever applies in the Has-NDIS=Yes branch — the
    // field isn't shown in the No branch, so it must never be silently written into the payload
    // via this field there.
    const prefillNdisNumber = this.hasNdis
      ? payload.ndisNumber || this.ndisNumber || null
      : payload.ndisNumber || null
    this.isNdisNumberPrefilled =
      this.hasNdis && !payload.ndisNumber && !!this.ndisNumber

    this.form = new FormGroup({
      ndisNumber: new FormControl<string | null>(prefillNdisNumber),
      planManagement: new FormControl<PlanManagement | null>(
        payload.planManagement,
      ),
      planManagerName: new FormControl<string | null>(payload.planManager.name),
      planManagerAddress: new FormControl<string | null>(
        payload.planManager.address,
      ),
      planManagerContactName: new FormControl<string | null>(
        payload.planManager.contactName,
      ),
      planManagerContactEmailPhone: new FormControl<string | null>(
        payload.planManager.contactEmailPhone,
      ),
      applyingForNdis: new FormControl<boolean | null>(payload.applyingForNdis),
      hasPhi: new FormControl<boolean | null>(
        payload.privateHealthInsurance.hasPhi,
      ),
      phiCountry: new FormControl<PhiCountry | null>(
        payload.privateHealthInsurance.country,
      ),
      phiInsurerName: new FormControl<string | null>(
        payload.privateHealthInsurance.insurerName,
      ),
      phiMemberNo: new FormControl<string | null>(
        payload.privateHealthInsurance.memberNo,
      ),
      startDate: new FormControl<Moment | null>(
        this.toMoment(payload.startDate),
      ),
      endDate: new FormControl<Moment | null>(this.toMoment(payload.endDate)),
    })

    if (this.hasNdis && prefillNdisNumber && !payload.ndisNumber) {
      // Persist the prefilled value into the payload immediately (AC8 — every field writes into
      // state), rather than waiting for the clinician to touch the field.
      this.writePayload()
    }

    this.subscription = this.form.valueChanges.subscribe(() => {
      if (!this.hasNdis) {
        this.recalculateEndDate()
      }
      this.writePayload()
    })
  }

  // AC10 — the shell's `ndisNumber` prefill source arrives asynchronously (a separate HTTP call,
  // service-plan-stepper.component.ts's loadNdisNumber()), which can resolve after this component
  // has already mounted and run its one-shot ngOnInit prefill. Apply a late-arriving value exactly
  // like the ngOnInit path would have, but only while the field is still empty and the clinician
  // hasn't touched it — never overwrite something they've already typed.
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.form || !this.hasNdis) {
      return
    }

    const change = changes['ndisNumber']
    if (!change || change.isFirstChange()) {
      return
    }

    const newValue = change.currentValue as string | null | undefined
    const control = this.form.controls.ndisNumber
    if (!newValue || control.value || control.touched) {
      return
    }

    control.setValue(newValue)
    this.isNdisNumberPrefilled = true
    this.writePayload()
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  get showPlanManagerPanel(): boolean {
    return this.form?.controls.planManagement.value === 'planManaged'
  }

  get showPhiDetails(): boolean {
    return this.form?.controls.hasPhi.value === true
  }

  get isEndDateReadOnly(): boolean {
    return !this.hasNdis
  }

  // ServicePlanStepComponent contract — Step 2 has no required fields (AC3): Continue is never
  // blocked here, and isLiveValid is intentionally omitted (shell default: not disabled).
  isValid(): boolean {
    return true
  }

  getValidationErrors(): ServicePlanFieldError[] {
    return []
  }

  private recalculateEndDate(): void {
    const startDate = this.form.controls.startDate.value
    const computedEndDate = startDate
      ? moment(startDate).add(12, 'months')
      : null
    const currentEndDate = this.form.controls.endDate.value

    const changed =
      (computedEndDate === null) !== (currentEndDate === null) ||
      (computedEndDate !== null &&
        currentEndDate !== null &&
        !computedEndDate.isSame(currentEndDate, 'day'))

    if (changed) {
      this.form.controls.endDate.setValue(computedEndDate, { emitEvent: false })
    }
  }

  private writePayload(): void {
    const value = this.form.getRawValue()
    const currentNdis = this.state.getPayload().ndis

    this.state.updatePayload({
      ndis: {
        ...currentNdis,
        ndisNumber: value.ndisNumber,
        planManagement: value.planManagement,
        planManager: {
          name: value.planManagerName,
          address: value.planManagerAddress,
          contactName: value.planManagerContactName,
          contactEmailPhone: value.planManagerContactEmailPhone,
        },
        applyingForNdis: value.applyingForNdis,
        privateHealthInsurance: {
          hasPhi: value.hasPhi,
          country: value.phiCountry,
          insurerName: value.phiInsurerName,
          memberNo: value.phiMemberNo,
        },
        startDate: this.toIsoDate(value.startDate),
        endDate: this.toIsoDate(value.endDate),
      },
    })
  }

  private toMoment(value: string | null): Moment | null {
    return value ? moment(value) : null
  }

  private toIsoDate(value: Moment | null): string | null {
    return value ? value.format('YYYY-MM-DD') : null
  }
}
